<template>
	<div class="tool">
		<p-button
			v-tooltip.top="'Add Users to Marketplace'"
			aria-label="Add Users to Marketplace"
			@click="show_add_users_modal = true"
		>
			<template #icon>
				<icon type="account-plus" size="20px" />
			</template>
		</p-button>
	</div>
	<p-dialog
		v-model:visible="show_add_users_modal"
		class="add-users-modal"
		@after-hide="reset"
		:modal="true"
		:dismissable-mask="true"
		:pt="{
			root: { class: 'w-12 sm:w-9 md:w-6' },
		}"
	>
		<template #header>
			<div class="flex gap-2 align-content-center align-items-center">
				<strong>Add Users to Marketplace</strong>
			</div>
		</template>
    <loader :show="loading"/>
		<div v-if="step === 'users'">
      <p>First you must select the users to add to the Marketplace: <strong>{{marketplace}}</strong></p>
      <p>Then you will need to select the role for the users.</p>
			<div class="control-group">
				<div class="inner">
					<label class="control-label">Select Users:</label>
					<div class="controls">
						<div class="field">
							<p-multi-select
								v-model="users"
								name="users"
								:options="admin_user_options"
								option-label="label"
								option-value="value"
								display-selected-label
								show-clear
								filter
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-else>
			<p>Select Roles for Users of this Marketplace ({{ marketplace }}):</p>
			<template v-for="user in users">
				<div class="control-group">
					<div class="inner">
						<div class="controls gap-10">
							<div class="field">
								<div class="user-label">{{ admin_users_map[user] }}</div>
							</div>
							<div class="field">
								<p-dropdown
									v-model="users_to_update[user]"
									name="role_id"
									:options="marketplace_role_options"
									option-label="label"
									option-value="value"
									placeholder="Select Role"
								/>
							</div>
							<p-button v-tooltip.top="'Remove User'" aria-label="Remove User" @click="handleDeleteUser(user)">
								<template #icon>
									<icon type="delete" size="20px" />
								</template>
							</p-button>
						</div>
					</div>
				</div>
			</template>
		</div>
		<template #footer>
			<div class="flex justify-content-between w-full">
				<p-button text label="Cancel" @click="show_add_users_modal = false" />
        <p-button v-if="step === 'users'" label="Select Roles" @click="handleSelectRoles" />
				<p-button v-if="step === 'roles'" label="Update Users Roles" @click="handleUpdateUsers(marketplace)" :loading="loading" />
			</div>
		</template>
	</p-dialog>
</template>

<script lang="ts">
import deleteAction from '@/components/widgets/DeleteAction.vue';
import { getInternalUsersAsOptions, updateUser } from '@GQL';
import { asyncForEach } from 'modern-async';

export default {
	name: 'AddUsersToMarketplace',
	components: [deleteAction],
	props: {
		marketplace: {
			type: String,
			required: true,
		},
		admin_user_options: {
			type: Object,
			required: true,
		},
		marketplace_role_options: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
      loading: false,
			show_add_users_modal: false,
			users: [],
			step: 'users',
			users_to_update: {},
		};
	},
	methods: {
		handleSelectRoles() {
			this.step = 'roles';
			this.users_to_update = this.users.reduce((acc, user) => {
				acc[user] = 'mp_admin';
				return acc;
			}, {});
		},
		handleDeleteUser(user) {
			this.users.splice(this.users.indexOf(user), 1);
			if (this.users.length === 0) {
				this.step = 'users';
				this.users_to_update = {};
			}
		},
		async handleUpdateUsers(mpid) {
      this.loading = true;
      try {
        const update_array = this.users.map((user) => ({
          user_id: user,
          role_id: this.users_to_update[user],
        }));

        await asyncForEach(update_array, async (user) => {
          const role_obj = {};
          role_obj[mpid] = user.role_id;
          await updateUser(user.user_id, {
            role_id: role_obj,
          }); // no literals here since we are merging in a completely new MPID
        });

        this.$toast.add({
          severity: 'success',
          summary: 'Users Updated',
          detail: 'Users have been updated successfully.',
          life: 7000
        });
        this.reset();
      }
      finally {
        this.loading = false;
      }
		},
		reset() {
			this.step = 'users';
			this.show_add_users_modal = false;
			this.users = [];
			this.users_to_update = {};
		},
	},
	computed: {
		admin_users_map() {
			return this.admin_user_options.reduce((acc, user) => {
				acc[user.value] = user.label;
				return acc;
			}, {});
		},
	},
};
</script>

<style scoped>
label {
	padding-left: 0.25rem;
}

.user-label {
	align-items: center;
	background-color: var(--gray-05);
	border: 1px solid var(--gray-15);
	border-radius: 3px;
	color: var(--gray-50);
	display: flex;
	height: 40px;
	padding: 0 0.75em;
}
</style>
